// Global Instructions Rule Applied!
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef, useMemo } from 'react'
import { Form, Row, Col, Spin } from 'antd'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { useSelector, useDispatch } from 'react-redux'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { useDrawer } from '../../../../core/contexts/DrawerContext.jsx'
import { updateActiveRecord } from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { selectLookupOptions, useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { useDealManager } from '../../hooks/useDealManager.ts'
import { CompanyTeamTable } from '../../../../core/components/CompanyTeamTable/index.tsx'
import { DealCommitments } from '../../../../core/components/DealCommitments/index.jsx'
import { useCommitDealGate } from '../../hooks/useCommitDealGate.tsx'
import { useOriginationExcludedDealType } from '../../hooks/useOriginationExcludedDealType.ts'
import { useStageMoveRights } from '../../../../core/hooks/useStageMoveRights.ts'
import { fetchCompanyById } from '../../../companies/services/companiesService.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { AccountSourcingFields, isSourcingField } from '../../../../core/components/AccountSourcingFields.tsx'
import type { CompanyFieldConfig } from '../../../../core/components/InputManager/types.ts'
import type { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'
import type { Currency } from '../../../currencies/interfaces.ts'
import { DealFields, validateDealRecord } from '../../../../state_manager/data_models/deal_record.ts'
import {
  getDealInfoFields,
  getRoundInfoFields,
  getNewCompanyFields,
  getDealTeamFields
} from '../../utils/fieldConfigs.ts'
import {
  transformFormDataToDealRecord,
  transformCompanyTeam,
  normalizeNewDealCommitments
} from '../../utils/dealFormTransformers.ts'
import {
  getInitialFormDataForNew,
  mergeDealIntoInitialFormData,
  getDefaultSelectedContacts,
  transformSystemUserToContact,
  transformUserEntryToContact as transformUserEntryToContactFromInit
} from '../../utils/dealFormInitializers.ts'
import { extractCompanyId } from '../../utils/dealFormUtils.ts'
import { fetchCompanySourcingContacts } from '../../../companies/services/companiesService.ts'
import { SECTION_ROUND_INFORMATION } from '../../../../state_manager/constants/strings.ts'
import {
  LOOKUP_CATEGORY_INDUSTRY,
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_DEAL_TYPE,
  LOOKUP_LABEL_DEAL_TYPE_SUBSCRIPTION
} from '../../../../state_manager/constants/lookupCategories.ts'
import { handleFieldChange as applyBusinessRules } from '../../utils/dealFormBusinessRules.ts'
import { validateRequiredFields } from '../../utils/dealFormValidation.ts'
import { summariseFieldErrors } from '../../../../core/utils/utilities.ts'
import { dismissErrorToast, showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../../core/components/FormTable/constants.ts'

// Keys for the drawer's error toasts, so they come down by themselves once
// the condition clears (validation passes / a later save succeeds / the
// drawer closes) instead of waiting for the ✕.
const REQUIRED_FIELDS_TOAST = 'deal-drawer:required-fields'
const SUBMIT_FAILED_TOAST = 'deal-drawer:submit-failed'

interface DrawerProps {
  deal?: any | null
  module?: string
  onSubmit?: (data: any) => Promise<void>
  loading?: boolean
}

const DEAL_TEAM_FIELD_TO_KEY: Record<string, 'introduced_by' | 'sourced_by' | 'deal_team_lead'> = {
  [DealFields.contact_accounts__introduced_by]: 'introduced_by',
  [DealFields.contact_accounts__sourced_by]: 'sourced_by',
  [DealFields.contact_deals__lead]: 'deal_team_lead'
}

/** Lookups the field-config builders (utils/fieldConfigs.ts) read from the drawer. */
interface DealFormHelpers {
  getCurrencyById: (id: string) => Currency | null
  getCurrencyOptions: () => SelectListEntry[]
  getPipelineStageOptions: () => SelectListEntry[]
  getLookupOptions: (category: string) => SelectListEntry[]
  isOriginationExcluded: (dealTypeId?: string | null) => boolean
  getCompanyDetailsById: CompanyFieldConfig['getCompanyDetailsById'] | null
}

/** The form sections a validation error can belong to. */
type ValidationSection = 'dealInfo' | 'roundInfo' | 'companyInfo' | 'dealTeam'
const VALIDATION_SECTIONS: ValidationSection[] = ['dealInfo', 'roundInfo', 'companyInfo', 'dealTeam']

/**
 * Deal Drawer Form Component for creating new deals only
 * Note: Editing existing deals is handled by DrawerExisting component
 */
export const Drawer = forwardRef<{ submit: () => Promise<void> }, DrawerProps>(
  ({ deal = null, module = 'deals', onSubmit, loading = false }, ref) => {
    const dispatch = useDispatch()
    const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()
    const { isNew } = useDrawer()
    const { getCurrencyById, getCurrencyOptions } = useCurrencies()
    const { defaultPipelineId } = useDealManager()

    const [form] = Form.useForm()

    const pipelineStages = useSelector((state: any) => state.globalConfigs?.pipeline_stages || [])
    const pipelineConfigs = useSelector((state: any) => state.globalConfigs?.pipeline_configs || [])
    const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])
    const currentUser = useSelector((state: any) => state.globalConfigs?.current_user || null)
    const currencyOptions = getCurrencyOptions()
    const fundingStageOptions = useSelector((state: any) => selectLookupOptions(state, LOOKUP_CATEGORY_FUNDING_STAGE))
    const industryOptions = useSelector((state: any) => selectLookupOptions(state, LOOKUP_CATEGORY_INDUSTRY))

    const tempRecordUuid = useMemo(() => {
      if (isNew) {
        return module === 'deal-pipeline' ? 'deal-pipeline-new' : 'deals-new'
      }
      return null
    }, [isNew, module])

    const { detailsByCategory, getLookupIdByLabel } = useLookups()

    // Default Deal Type to Subscription, resolved by label (mirrors the backend default).
    // If lookups aren't loaded the field stays empty + required; backend still defaults.
    const dealTypeDefaults = useMemo(() => {
      const id = getLookupIdByLabel(LOOKUP_LABEL_DEAL_TYPE_SUBSCRIPTION, LOOKUP_CATEGORY_DEAL_TYPE)
      return id ? { [DealFields.deals__deal_type]: id } : {}
    }, [getLookupIdByLabel])

    const initialMergeRef = useRef(mergeDealIntoInitialFormData(deal, dealTypeDefaults))
    const initialMerge = initialMergeRef.current
    const [formData, setFormData] = useState<Record<string, any>>(initialMerge.formData)

    // A new deal may only be created into a stage the user may move deals into.
    const applyStageMoveRights = useStageMoveRights()

    const pipelineStageOptions = useMemo(() => {
      const dealPipelineId = formData[DealFields.deals__pipeline_origination] || defaultPipelineId

      if (!dealPipelineId || !pipelineConfigs.length || !pipelineStages.length) {
        return []
      }

      const filteredConfigs = pipelineConfigs.filter(
        (config: any) => config.pipeline === dealPipelineId && config.visible_tbl === true
      )

      const allPipelineStageOptions = filteredConfigs
        .map((config: any) => {
          const stage = pipelineStages.find((s: any) => s.id === config.stage)
          return {
            value: config.id,
            label: stage?.name || '',
            sequence: config.sequence_tbl || config.sequence_kb || 0
          }
        })
        .filter((option: any) => option.label)
        .sort((a: any, b: any) => a.label.localeCompare(b.label))

      return applyStageMoveRights(allPipelineStageOptions, null)
    }, [formData, defaultPipelineId, pipelineConfigs, pipelineStages, applyStageMoveRights])

    // Deal types barred from Deal Origination get no Pipeline Stage field —
    // the backend drops any stage supplied for these types regardless.
    const isOriginationExcluded = useOriginationExcludedDealType()

    const helpers = useMemo<DealFormHelpers>(
      () => ({
        getCurrencyById,
        getCurrencyOptions: () => currencyOptions,
        getPipelineStageOptions: () => pipelineStageOptions,
        getLookupOptions: (category: string) => {
          if (category === LOOKUP_CATEGORY_FUNDING_STAGE) return fundingStageOptions
          if (category === LOOKUP_CATEGORY_INDUSTRY) return industryOptions
          return []
        },
        isOriginationExcluded,
        getCompanyDetailsById: null
      }),
      [
        getCurrencyById,
        currencyOptions,
        pipelineStageOptions,
        fundingStageOptions,
        industryOptions,
        isOriginationExcluded
      ]
    )

    const companyLookupId =
      detailsByCategory?.['Global Configs']?.find((option: any) => option.label === 'internal_company_id')?.value ||
      null

    const [commitments, setCommitments] = useState<any[]>(initialMerge.commitments)

    // Commit gate: creating a deal straight into an origination Committed stage
    // without a commitment / security type / valuation prompts first. Kept in a
    // ref because handleSubmit deliberately has no dependencies.
    const { gate: commitGate, isCommitTarget, holder: commitGateHolder } = useCommitDealGate()
    const commitGateRef = useRef({ gate: commitGate, isCommitTarget })
    commitGateRef.current = { gate: commitGate, isCommitTarget }
    const [companyTeamMembers, setCompanyTeamMembers] = useState<any[]>(initialMerge.companyTeamMembers)

    const getTeamMembersForAPIRef = useRef<((tempRecordId: string) => any[]) | null>(null)
    const dealCommitmentsRef = useRef<any>(null)
    const companyTeamRef = useRef<any>(null)
    const prevCompanyIdRef = useRef<string | number | null>(formData[DealFields.deals__account] || null)
    const prevIsNewAccountRef = useRef<boolean>(formData[DealFields.prog__is_new_account] || false)

    const [selectedContacts, setSelectedContacts] = useState<{
      introduced_by: any[]
      sourced_by: any[]
      deal_team_lead: any[]
    }>(() =>
      deal && Object.keys(deal).length > 0 ? initialMerge.selectedContacts : getDefaultSelectedContacts(currentUser)
    )

    const [dealTeamLoading, setDealTeamLoading] = useState<boolean>(false)

    const transformUserEntryToContact = useCallback(
      (userEntry: any) => transformUserEntryToContactFromInit(userEntry, true),
      []
    )

    // Switching to an excluded deal type retires any stage already chosen (the
    // drawer opened from a board column, or the Subscription default landed
    // one) so the now-hidden field cannot submit a stale value. The backend
    // drops it too, but leaving it in formData would misreport what was saved.
    useEffect(() => {
      if (!isOriginationExcluded(formData[DealFields.deals__deal_type])) return
      if (!formData[DealFields.pipeline_deals__stage] && !formData[DealFields.pipeline_deals__stage_date]) return
      setFormData((current) => ({
        ...current,
        [DealFields.pipeline_deals__stage]: undefined,
        [DealFields.pipeline_deals__stage_date]: undefined
      }))
      form.setFieldsValue({ [DealFields.pipeline_deals__stage]: undefined })
    }, [formData, isOriginationExcluded, form])

    const dealInfoFields = useMemo(() => getDealInfoFields(formData, helpers), [formData, helpers])
    const roundInfoFields = useMemo(() => getRoundInfoFields(formData, helpers), [formData, helpers])
    const newCompanyFields = useMemo(() => getNewCompanyFields(formData, helpers), [formData, helpers])
    // An existing company owns its Introduced by / Sourced by links: the
    // drawer shows them read-only, does not send them, and points the user at
    // the company form to change them.
    const sourcingAccountId = formData[DealFields.prog__is_new_account]
      ? null
      : extractCompanyId(formData[DealFields.deals__account] as any)
    const sourcingLocked = !!sourcingAccountId
    const dealTeamFields = useMemo(
      () => getDealTeamFields(formData, selectedContacts, companyLookupId),
      [formData, selectedContacts, companyLookupId]
    )

    const [validationErrors, setValidationErrors] = useState<Record<ValidationSection, Record<string, string>>>({
      dealInfo: {},
      roundInfo: {},
      companyInfo: {},
      dealTeam: {}
    })

    // Which section holds the first validation error, and a counter bumped
    // on every Save click. The InputManager of that section receives the
    // counter as validationErrorsVersion and re-scrolls/focuses the field —
    // the top rows of Deal Information sit under a sticky header once the
    // drawer is scrolled, so without this a failed Save is invisible.
    const [errorFocus, setErrorFocus] = useState<{
      section: 'dealInfo' | 'dealTeam' | 'roundInfo' | 'companyInfo' | null
      version: number
    }>({ section: null, version: 0 })

    const handleCommitmentsChange = (updatedCommitments: any[]) => {
      setCommitments(updatedCommitments)
    }

    const isInitializingRef = useRef<boolean>(false)
    const lastSyncedDataRef = useRef<string | null>(null)

    const formDataRef = useRef<Record<string, any>>(formData)
    const commitmentsRef = useRef<any[]>(commitments)
    const selectedContactsRef = useRef<{
      introduced_by: any[]
      sourced_by: any[]
      deal_team_lead: any[]
    }>(selectedContacts)
    const companyTeamMembersRef = useRef<any[]>(companyTeamMembers)

    useEffect(() => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
        syncTimeoutRef.current = null
      }

      if (isNew && deal && Object.keys(deal).length > 0) {
        isInitializingRef.current = true
        const {
          formData: mergedData,
          commitments: tempCommitments,
          selectedContacts: tempSelectedContacts,
          companyTeamMembers: tempCompanyTeamMembers
        } = mergeDealIntoInitialFormData(deal, dealTypeDefaults)

        formDataRef.current = mergedData
        commitmentsRef.current = tempCommitments
        selectedContactsRef.current = tempSelectedContacts
        companyTeamMembersRef.current = tempCompanyTeamMembers

        setFormData(mergedData)
        form.setFieldsValue(mergedData)
        setCommitments(tempCommitments)
        setSelectedContacts(tempSelectedContacts)
        setCompanyTeamMembers(tempCompanyTeamMembers)

        lastSyncedDataRef.current = JSON.stringify({
          ...mergedData,
          commitments: tempCommitments,
          selectedContacts: tempSelectedContacts,
          companyTeamMembers: tempCompanyTeamMembers
        })

        setTimeout(() => {
          isInitializingRef.current = false
        }, 100)
      } else if (isNew && (!deal || Object.keys(deal).length === 0)) {
        const initialData = getInitialFormDataForNew(dealTypeDefaults)
        const defaultContacts = getDefaultSelectedContacts(currentUser)
        setFormData(initialData)
        form.setFieldsValue(initialData)
        setCommitments([])
        setSelectedContacts(defaultContacts)
        setCompanyTeamMembers([])
        lastSyncedDataRef.current = JSON.stringify({
          ...initialData,
          commitments: [],
          selectedContacts: defaultContacts,
          companyTeamMembers: []
        })
      }
    }, [deal, isNew, form, tempRecordUuid, currentUser, dealTypeDefaults])

    useEffect(() => {
      formDataRef.current = formData
      commitmentsRef.current = commitments
      selectedContactsRef.current = selectedContacts
      companyTeamMembersRef.current = companyTeamMembers
    }, [formData, commitments, selectedContacts, companyTeamMembers])

    const fetchAndSetSourcingContacts = useCallback(
      async (companyId: string | number) => {
        setDealTeamLoading(true)
        try {
          const response = await fetchCompanySourcingContacts(companyId)
          if (response.data && !response.error) {
            const introducedByContacts = (response.data.contact_accounts__introduced_by || []).map(
              transformUserEntryToContact
            )
            const sourcedByContacts = (response.data.contact_accounts__sourced_by || []).map(
              transformUserEntryToContact
            )
            setSelectedContacts((prev) => ({
              ...prev,
              introduced_by: introducedByContacts,
              sourced_by: sourcedByContacts
            }))
          }
        } catch (error) {
          console.error('Error fetching company sourcing contacts:', error)
        } finally {
          setDealTeamLoading(false)
        }
      },
      [transformUserEntryToContact]
    )

    // Introduced by / Sourced by follow the existing company: loaded whenever
    // the drawer comes to hold one - picked in the form, handed in with a seed,
    // or restored with a draft (whose copy may be out of date).
    const loadedSourcingAccountIdRef = useRef<string | number | null>(null)
    useEffect(() => {
      if (loadedSourcingAccountIdRef.current === sourcingAccountId) return
      loadedSourcingAccountIdRef.current = sourcingAccountId
      if (sourcingAccountId) {
        void fetchAndSetSourcingContacts(sourcingAccountId)
      }
    }, [sourcingAccountId, fetchAndSetSourcingContacts])

    const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
      if (!tempRecordUuid || !isNew || isInitializingRef.current) {
        return
      }

      const latestFormData = formDataRef.current
      const currentData = {
        ...latestFormData,
        commitments: commitmentsRef.current,
        selectedContacts: selectedContactsRef.current,
        companyTeamMembers: companyTeamMembersRef.current
      }
      const currentDataString = JSON.stringify(currentData)

      if (currentDataString === lastSyncedDataRef.current) {
        return
      }

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }

      syncTimeoutRef.current = setTimeout(() => {
        if (!isInitializingRef.current) {
          const latestData = {
            ...formDataRef.current,
            commitments: commitmentsRef.current,
            selectedContacts: selectedContactsRef.current,
            companyTeamMembers: companyTeamMembersRef.current
          }
          const latestDataString = JSON.stringify(latestData)

          dispatch(
            updateActiveRecord({
              uuid: tempRecordUuid,
              updates: latestData
            })
          )
          lastSyncedDataRef.current = latestDataString
        }
      }, 300)

      return () => {
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current)
        }
      }
    }, [formData, commitments, selectedContacts, companyTeamMembers, tempRecordUuid, isNew, dispatch])

    useEffect(() => {
      setRightMenuWidth('42%')
      return () => {
        resetRightMenuWidth()
      }
    }, [setRightMenuWidth, resetRightMenuWidth])

    useEffect(() => {
      if (currentUser) {
        const currentUserContact = transformSystemUserToContact(currentUser)
        setSelectedContacts((prev) => {
          const hasSourcedBy = prev.sourced_by?.length > 0
          const hasLead = prev.deal_team_lead?.length > 0

          if (!hasSourcedBy || !hasLead) {
            return {
              ...prev,
              sourced_by: hasSourcedBy ? prev.sourced_by : [currentUserContact],
              deal_team_lead: hasLead ? prev.deal_team_lead : [currentUserContact]
            }
          }
          return prev
        })
      }
    }, [currentUser])

    const handleSubmit = useCallback(async () => {
      try {
        await form.validateFields()

        const formValues = form.getFieldsValue()

        const hasPendingCommitments = dealCommitmentsRef.current?.hasPendingCommitments?.() || false
        if (hasPendingCommitments) {
          showErrorToast('Please save or remove any pending deal commitments before creating the deal', TOAST_CONFIG)
          return
        }

        if (formData[DealFields.prog__is_new_account]) {
          const hasPendingTeamMembers = companyTeamRef.current?.hasPendingTeamMembers?.() || false
          if (hasPendingTeamMembers) {
            showErrorToast(
              'Please save or remove any pending company team members before creating the deal',
              TOAST_CONFIG
            )
            return
          }
        }

        const dealInfoFields = getDealInfoFields(formData, helpers)
        const dealInfoValidation = validateRequiredFields(dealInfoFields, formValues, formData)

        const roundInfoFields = formData[DealFields.rounds__is_funding_round]
          ? getRoundInfoFields(formData, helpers)
          : []
        const roundInfoValidation = validateRequiredFields(roundInfoFields, formValues, formData)

        const companyInfoFields = formData[DealFields.prog__is_new_account]
          ? getNewCompanyFields(formData, helpers)
          : []
        const companyInfoValidation = validateRequiredFields(companyInfoFields, formValues, formData)

        const dealTeamFields = getDealTeamFields(formData, selectedContacts, companyLookupId)
        const dealTeamValidation = validateRequiredFields(dealTeamFields, formValues, formData, selectedContacts)

        const allValid =
          dealInfoValidation.isValid &&
          roundInfoValidation.isValid &&
          companyInfoValidation.isValid &&
          dealTeamValidation.isValid

        setValidationErrors({
          dealInfo: dealInfoValidation.errors,
          roundInfo: roundInfoValidation.errors,
          companyInfo: companyInfoValidation.errors,
          dealTeam: dealTeamValidation.errors
        })

        if (!allValid) {
          // Per-field red text is painted by InputManager; the toast names the
          // fields for when they are scrolled out of view, and the version bump
          // makes the owning InputManager scroll the first one into view.
          const sectionErrors = [
            ['dealInfo', dealInfoValidation.errors],
            ['dealTeam', dealTeamValidation.errors],
            ['roundInfo', roundInfoValidation.errors],
            ['companyInfo', companyInfoValidation.errors]
          ] as const
          const firstSection = sectionErrors.find(([, errors]) => Object.keys(errors).length > 0)?.[0] ?? null
          const message = summariseFieldErrors(sectionErrors.map(([, errors]) => errors))
          showErrorToast(message || 'Please complete the required fields', {
            ...TOAST_CONFIG,
            key: REQUIRED_FIELDS_TOAST
          })
          setErrorFocus((prev) => ({ section: firstSection, version: prev.version + 1 }))
          return
        }
        // Validation passed — the required-fields toast (if any) is stale.
        dismissErrorToast(REQUIRED_FIELDS_TOAST)

        // Commit gate. The gate skips a record already at the target stage, and
        // this form's data carries the stage being chosen — hand it in blank.
        const currentCommitments: any[] = commitmentsRef.current || []
        const commitmentTotal = currentCommitments.reduce(
          (sum: number, row: any) => sum + (Number(row?.amount) > 0 ? Number(row.amount) : 0),
          0
        )
        // Industry and Critical Technology Area: a new company carries both in
        // the form; an existing one only by id, so read the account — but only
        // when the chosen stage is a commit target, so an ordinary save costs
        // no extra request. One fetch serves both fields.
        const targetStage = formDataRef.current[DealFields.pipeline_deals__stage]
        const gateData = { ...formDataRef.current, [DealFields.pipeline_deals__stage]: null }
        const isNewAccount = formDataRef.current[DealFields.prog__is_new_account]
        let industry: string | null = isNewAccount
          ? formDataRef.current[DealFields.accounts__industry] || null
          : null
        let criticalTechnologyArea: string | null = isNewAccount
          ? formDataRef.current[DealFields.accounts__critical_technology_area] || null
          : null
        const companyId = extractCompanyId(formDataRef.current[DealFields.deals__account] as any)
        if (!isNewAccount && companyId && commitGateRef.current.isCommitTarget(targetStage)) {
          const company = await fetchCompanyById(companyId, getAccessToken(store.getState().auth))
          const companyData = company.data as Record<string, any> | null
          industry = (companyData?.[DealFields.accounts__industry] as string) || null
          criticalTechnologyArea =
            (companyData?.[DealFields.accounts__critical_technology_area] as string) || null
        }
        // The drawer's un-saved commitment rows are its own shape ({fund, amount}),
        // so the gate cannot read the funds off the record — hand them in.
        const fundIds = currentCommitments.map((row: any) => row?.fund).filter(Boolean)
        const proceed = await commitGateRef.current.gate(gateData, targetStage, {
          inline: false,
          commitmentAmount: commitmentTotal,
          industry,
          criticalTechnologyArea,
          fundIds
        })
        if (!proceed) return

        const getTeamMembersFn =
          formData[DealFields.prog__is_new_account] && getTeamMembersForAPIRef.current
            ? () => {
                const tempRecordId = crypto.randomUUID()
                return getTeamMembersForAPIRef.current!(tempRecordId)
              }
            : null

        const dealData = transformFormDataToDealRecord(
          formData,
          formValues,
          commitments,
          // The company's own links are not the drawer's to write.
          sourcingLocked ? { ...selectedContacts, introduced_by: [], sourced_by: [] } : selectedContacts,
          getTeamMembersFn,
          formData[DealFields.prog__is_new_account]
        )

        if (!dealData.deals__id) {
          dealData.deals__id = crypto.randomUUID()
        }

        if (dealData[DealFields.prog__deal_commitments] && Array.isArray(dealData[DealFields.prog__deal_commitments])) {
          dealData[DealFields.prog__deal_commitments] = normalizeNewDealCommitments(
            dealData[DealFields.prog__deal_commitments] as any[]
          )
        }

        if (
          dealData[DealFields.prog__company_team] &&
          Array.isArray(dealData[DealFields.prog__company_team]) &&
          !formData[DealFields.prog__is_new_account]
        ) {
          dealData[DealFields.prog__company_team] = transformCompanyTeam(
            dealData[DealFields.prog__company_team] as any[]
          )
        }

        try {
          validateDealRecord(dealData)
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Validation failed'
          showErrorToast(`Validation failed: ${errorMessage}`, TOAST_CONFIG)
          console.error('Deal record validation failed:', error)
          return
        }

        if (onSubmit) {
          await onSubmit(dealData)
        }
      } catch (error) {
        // A Save that cannot complete must always say so (owner rule
        // 2026-09-02) — never a console-only failure.
        const antdErrorFields = (error as { errorFields?: Array<{ errors?: string[] }> })?.errorFields
        const message = Array.isArray(antdErrorFields)
          ? antdErrorFields.map((f) => (f.errors || []).join(', ')).join('; ')
          : error instanceof Error
            ? error.message
            : ''
        showErrorToast(message || 'The deal could not be saved — please try again', {
          ...TOAST_CONFIG,
          key: SUBMIT_FAILED_TOAST
        })
        console.error('Deal form submit failed:', error)
      }
    }, [form, formData, commitments, selectedContacts, onSubmit, helpers, companyLookupId, sourcingLocked])

    // Closing the drawer (cancel or success) retires its error toasts — the
    // conditions they described no longer apply to anything on screen.
    useEffect(
      () => () => {
        dismissErrorToast(REQUIRED_FIELDS_TOAST)
        dismissErrorToast(SUBMIT_FAILED_TOAST)
      },
      []
    )

    useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit
      }),
      [handleSubmit]
    )

    const handleFieldChange = useCallback(
      async (field: string, value: any) => {
        let updatedFormData: Record<string, any> | null = null
        let isNewAccountValue: boolean | null = null
        let companyIdValue: string | number | null = null

        setFormData((currentFormData) => {
          const result = applyBusinessRules(field, value, currentFormData, form)

          updatedFormData = result.formData
          isNewAccountValue = updatedFormData[DealFields.prog__is_new_account]
          companyIdValue = extractCompanyId(updatedFormData[DealFields.deals__account] as any)

          if (result.formData[DealFields.accounts__business_country_code] && countriesData.length > 0) {
            const countryCode = result.formData[DealFields.accounts__business_country_code]
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updatedFormData[DealFields.accounts__business_country_label] = countryLabel
              if (result.formUpdates) {
                result.formUpdates[DealFields.accounts__business_country_label] = countryLabel
              } else {
                result.formUpdates = { [DealFields.accounts__business_country_label]: countryLabel }
              }
            }
          }

          const valueChanged = updatedFormData[field] !== currentFormData[field]
          if (!valueChanged && !result.formUpdates) {
            return currentFormData
          }

          if (result.formUpdates) {
            form.setFieldsValue(result.formUpdates)
          }

          setValidationErrors((prev) => {
            const updated = { ...prev }
            VALIDATION_SECTIONS.forEach((section) => {
              if (updated[section][field]) {
                const { [field]: removed, ...rest } = updated[section]
                updated[section] = rest
              }
            })
            return updated
          })

          return updatedFormData
        })

        if (updatedFormData) {
          formDataRef.current = updatedFormData
        }

        const prevIsNewAccount = prevIsNewAccountRef.current

        if (field === DealFields.prog__is_new_account && value === true && prevIsNewAccount !== true) {
          setSelectedContacts(getDefaultSelectedContacts(currentUser))
          prevIsNewAccountRef.current = true
          prevCompanyIdRef.current = companyIdValue ?? null
          return
        }

        if (field === DealFields.prog__is_new_account && value === false && prevIsNewAccount === true) {
          setSelectedContacts(getDefaultSelectedContacts(currentUser, { sourcedBy: false }))
          prevIsNewAccountRef.current = false

          if (!companyIdValue) {
            setSelectedContacts(getDefaultSelectedContacts(currentUser))
          }
          prevCompanyIdRef.current = companyIdValue ?? null
          return
        }

        if (field === DealFields.deals__account && !isNewAccountValue) {
          if (!value) {
            const defaults = getDefaultSelectedContacts(currentUser, { dealTeamLead: false })
            setSelectedContacts((prev) => ({ ...prev, introduced_by: [], sourced_by: defaults.sourced_by }))
          }
          prevCompanyIdRef.current = extractCompanyId(value) ?? null
          return
        }

        if (field === DealFields.prog__is_new_account) {
          prevIsNewAccountRef.current = isNewAccountValue || false
        }
        if (field === DealFields.deals__account) {
          prevCompanyIdRef.current = extractCompanyId(value) ?? null
        }
      },
      [form, countriesData, currentUser]
    )

    return (
      <>
        {commitGateHolder}
        <div data-debug='drawer-new-container' data-component='DrawerNew' className='space-y-1'>
          <Form
            data-debug='drawer-new-form'
            form={form}
            layout='vertical'
            className='form-compact'
            initialValues={formData}
          >
            <Row data-debug='drawer-new-main-row' gutter={12}>
              <Col span={12}>
                <FormSection title='Deal Information' className='mb-2'>
                  <div data-debug='drawer-new-deal-info-section'>
                    <InputManager
                      inputMode='edit'
                      labelPosition='above'
                      fields={dealInfoFields}
                      onChange={(key: string, value: any) => handleFieldChange(key, value)}
                      validationErrors={validationErrors.dealInfo}
                      validationErrorsVersion={errorFocus.section === 'dealInfo' ? errorFocus.version : undefined}
                      className='deal-info-input-manager'
                    />
                  </div>
                </FormSection>

                <FormSection title='Deal Team' className='mb-2'>
                  <Spin spinning={dealTeamLoading}>
                    <div data-debug='drawer-new-deal-team-section'>
                      {sourcingLocked && (
                        <AccountSourcingFields
                          teamFields={dealTeamFields}
                          account={sourcingAccountId}
                          accountNoun='company'
                          inputMode='edit'
                          labelPosition='above'
                          className='deal-team-sourcing-input-manager'
                        />
                      )}
                      <InputManager
                        inputMode='edit'
                        labelPosition='above'
                        fields={
                          sourcingLocked
                            ? dealTeamFields.filter((field) => !isSourcingField(field))
                            : dealTeamFields
                        }
                        onChange={(key: string, value: any) => {
                          const contactKey = DEAL_TEAM_FIELD_TO_KEY[key]
                          if (contactKey) {
                            setSelectedContacts((prev) => ({ ...prev, [contactKey]: value }))
                          }
                          setValidationErrors((prev) => {
                            if (!prev.dealTeam[key]) return prev
                            const { [key]: _, ...rest } = prev.dealTeam
                            return { ...prev, dealTeam: rest }
                          })
                        }}
                        validationErrors={validationErrors.dealTeam}
                        validationErrorsVersion={errorFocus.section === 'dealTeam' ? errorFocus.version : undefined}
                        className='deal-team-input-manager'
                      />
                    </div>
                  </Spin>
                </FormSection>

                <DealCommitments
                  ref={dealCommitmentsRef}
                  mode='new'
                  initialCommitments={commitments}
                  onChange={handleCommitmentsChange}
                  currencySymbol={getCurrencyById(formData[DealFields.deals__currency])?.symbol || '$'}
                  showTotal={true}
                  recordId={null}
                />
              </Col>

              <Col span={12}>
                {formData[DealFields.rounds__is_funding_round] && (
                  <FormSection title={SECTION_ROUND_INFORMATION} className='mb-2'>
                    <div data-debug='drawer-new-round-info-section'>
                      <InputManager
                        inputMode='edit'
                        labelPosition='above'
                        fields={roundInfoFields}
                        onChange={(key: string, value: any) => handleFieldChange(key, value)}
                        validationErrors={validationErrors.roundInfo}
                        validationErrorsVersion={errorFocus.section === 'roundInfo' ? errorFocus.version : undefined}
                        className='round-info-input-manager'
                      />
                    </div>
                  </FormSection>
                )}

                {formData[DealFields.prog__is_new_account] && (
                  <FormSection title='New Company Information' className='mb-2' titleColor='green'>
                    <div data-debug='drawer-new-company-info-section'>
                      <InputManager
                        inputMode='edit'
                        labelPosition='above'
                        fields={newCompanyFields}
                        onChange={(key: string, value: any) => handleFieldChange(key, value)}
                        validationErrors={validationErrors.companyInfo}
                        validationErrorsVersion={errorFocus.section === 'companyInfo' ? errorFocus.version : undefined}
                        className='company-info-input-manager'
                      />
                    </div>
                  </FormSection>
                )}
              </Col>
            </Row>

            {formData[DealFields.prog__is_new_account] && (
              <Row data-debug='drawer-new-company-team-row' className='mt-2'>
                <Col span={24}>
                  <CompanyTeamTable
                    ref={companyTeamRef}
                    mode='new'
                    initialTeamMembers={companyTeamMembers}
                    onChange={(updatedMembers: any[]) => {
                      setCompanyTeamMembers(updatedMembers)
                    }}
                    getCurrentTeamMembers={(getApiDataFn: (tempRecordId: string) => any[]) => {
                      getTeamMembersForAPIRef.current = getApiDataFn
                    }}
                  />
                </Col>
              </Row>
            )}
          </Form>
        </div>
      </>
    )
  }
)

Drawer.displayName = 'DealDrawerNew'
